// Parser for Apple 2703(d) returns: Apple's production ZIP (legal request,
// declaration, the Apple data workbook and its data dictionary) and the
// per-account ZIP that the Apple Legal Process Download tool makes (Account
// Information, Logs - iCloud / iMessage / FaceTime / iCloud Mail, iCloud Mail
// headers). Everything runs in the browser; nothing is uploaded.
//
// Apple's sheets all follow one layout, so one reader handles them: a few
// title lines ("Apple Inc.", the report name, "DSID: …" or "Phone Number: …",
// "Period: …", "Timezone: …"), footnotes, then a header row and data rows.

import JSZip from 'jszip';
import ExcelJS from 'exceljs';
import { parseTime, isIp, cleanHandle } from './analysisKit';

// Shared with the other parsers (see analysisKit.js); re-exported under the names used here.
export { isIp, cleanHandle };
export const parseAppleTime = parseTime;

// ─── Reading the inputs ──────────────────────────────────────────────────────

const SKIP_NAMES = /(^|\/)(__MACOSX|\.DS_Store|\._)/;

// Turns dropped/selected files (ZIPs, a folder, or loose files) into a flat
// list of { path, name, size, buffer() }, opening ZIPs (and ZIPs in ZIPs).
export async function collectEntries(fileList, onProgress = () => {}) {
  const out = [];
  const files = Array.from(fileList);
  for (let i = 0; i < files.length; i++) {
    const f = files[i];
    const path = f.webkitRelativePath || f.name;
    onProgress(`Reading ${f.name}`);
    if (/\.zip$/i.test(f.name)) {
      await addZip(await f.arrayBuffer(), `${f.name}/`, out, 0, f.name);
    } else if (!SKIP_NAMES.test(path)) {
      out.push({ path, name: f.name, size: f.size, source: path.split('/')[0], buffer: () => f.arrayBuffer() });
    }
  }
  return out;
}

async function addZip(buf, prefix, out, depth, source) {
  const zip = await JSZip.loadAsync(buf);
  const entries = Object.values(zip.files).filter(e => !e.dir && !SKIP_NAMES.test(e.name));
  for (const e of entries) {
    const name = e.name.split('/').pop();
    if (/\.zip$/i.test(name) && depth < 3) {
      try {
        await addZip(await e.async('arraybuffer'), `${prefix}${e.name}/`, out, depth + 1, source);
        continue;
      } catch {
        // Not a readable ZIP (e.g. password-protected): list it as a file.
      }
    }
    out.push({
      path: prefix + e.name, name, size: e._data?.uncompressedSize || 0, source,
      buffer: () => e.async('arraybuffer'),
    });
  }
}

// ─── Cells and rows ──────────────────────────────────────────────────────────

const pad = n => String(n).padStart(2, '0');

function cellText(v) {
  if (v == null) return '';
  if (v instanceof Date) {
    // Excel stores wall-clock time; ExcelJS hands it back as if it were UTC.
    if (isNaN(v)) return '';
    const t = `${pad(v.getUTCHours())}:${pad(v.getUTCMinutes())}:${pad(v.getUTCSeconds())}`;
    return `${v.getUTCFullYear()}-${pad(v.getUTCMonth() + 1)}-${pad(v.getUTCDate())}${t === '00:00:00' ? '' : ' ' + t}`;
  }
  if (typeof v === 'object') {
    if (Array.isArray(v.richText)) return v.richText.map(r => r.text).join('').trim();
    if ('text' in v) return cellText(v.text);
    if ('result' in v) return cellText(v.result);
    if ('error' in v) return '';
    return '';
  }
  if (typeof v === 'number') return Number.isInteger(v) ? v.toFixed(0) : String(v);
  if (typeof v === 'boolean') return v ? 'TRUE' : 'FALSE';
  return String(v).trim();
}

// ─── XLSX, read straight from its XML ────────────────────────────────────────
// The same approach as tools/apple_2703d_structure.py, which reads Apple's real
// workbooks fully. ExcelJS is only a fallback for files this can't open.

const XML_ENT = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'" };
const unxml = s => s.replace(/&(#x[0-9a-f]+|#\d+|\w+);/gi, (m, e) => (e[0] === '#'
  ? String.fromCodePoint(e[1].toLowerCase() === 'x' ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10))
  : XML_ENT[e] ?? m));
const attrs = tag => Object.fromEntries([...tag.matchAll(/([\w:]+)="([^"]*)"/g)].map(m => [m[1], unxml(m[2])]));
// Text of <t> elements, leaving out phonetic runs (<rPh>).
const tText = xml => [...xml.replace(/<(\w+:)?rPh\b[\s\S]*?<\/(\w+:)?rPh>/g, '')
  .matchAll(/<(?:\w+:)?t(?:\s[^>]*)?>([\s\S]*?)<\/(?:\w+:)?t>/g)].map(m => unxml(m[1])).join('');

const BUILTIN_DATE_FMTS = new Set([14, 15, 16, 17, 18, 19, 20, 21, 22, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 45, 46, 47, 50, 51, 52, 53, 54, 55, 56, 57, 58]);

function serialToText(n) {
  const d = new Date(Math.round((n - 25569) * 86400000));
  if (isNaN(d)) return String(n);
  const t = `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
  if (n < 1) return t;
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}${t === '00:00:00' ? '' : ' ' + t}`;
}

const colIndex = ref => [...(ref.match(/^[A-Z]+/) || [''])[0]].reduce((n, ch) => n * 26 + ch.charCodeAt(0) - 64, 0) - 1;

async function readWorkbookXml(buf) {
  const zip = await JSZip.loadAsync(buf);
  const read = async name => {
    const f = zip.file(name) || zip.file(Object.keys(zip.files).find(k => k.toLowerCase() === name.toLowerCase()) || '');
    return f ? f.async('string') : '';
  };
  const shared = [...(await read('xl/sharedStrings.xml')).matchAll(/<(?:\w+:)?si>([\s\S]*?)<\/(?:\w+:)?si>/g)].map(m => tText(m[1]));

  const styles = await read('xl/styles.xml');
  const custom = Object.fromEntries([...styles.matchAll(/<(?:\w+:)?numFmt\b[^>]*>/g)].map(m => attrs(m[0])).map(a => [Number(a.numFmtId), a.formatCode || '']));
  const xfs = ((styles.match(/<(?:\w+:)?cellXfs\b[^>]*>([\s\S]*?)<\/(?:\w+:)?cellXfs>/) || [])[1] || '');
  const dateStyle = [...xfs.matchAll(/<(?:\w+:)?xf\b[^>]*>/g)].map(m => {
    const id = Number(attrs(m[0]).numFmtId || 0);
    if (BUILTIN_DATE_FMTS.has(id)) return true;
    const code = (custom[id] || '').replace(/"[^"]*"|\[[^\]]*\]|\\./g, '');
    return /[ydhs]/i.test(code);
  });

  const rels = Object.fromEntries([...(await read('xl/_rels/workbook.xml.rels')).matchAll(/<(?:\w+:)?Relationship\b[^>]*>/g)]
    .map(m => attrs(m[0])).map(a => [a.Id, a.Target]));
  const sheets = [];
  for (const m of (await read('xl/workbook.xml')).matchAll(/<(?:\w+:)?sheet\b[^>]*>/g)) {
    const a = attrs(m[0]);
    const target = rels[a['r:id'] || Object.entries(a).find(([k]) => /:id$/.test(k))?.[1]] || '';
    const xml = await read(target.startsWith('/') ? target.slice(1) : `xl/${target.replace(/^\.\//, '')}`);
    const rows = [];
    let next = 0;
    for (const rm of xml.matchAll(/<(?:\w+:)?row\b([^>]*?)(?:\/>|>([\s\S]*?)<\/(?:\w+:)?row>)/g)) {
      const r = Number(attrs(rm[1]).r || next + 1) - 1;
      next = r + 1;
      const vals = [];
      let col = 0;
      for (const cm of (rm[2] || '').matchAll(/<(?:\w+:)?c\b([^>]*?)(?:\/>|>([\s\S]*?)<\/(?:\w+:)?c>)/g)) {
        const ca = attrs(cm[1]);
        col = ca.r ? colIndex(ca.r) : col;
        const inner = cm[2] || '';
        const v = unxml((inner.match(/<(?:\w+:)?v>([\s\S]*?)<\/(?:\w+:)?v>/) || [])[1] || '');
        let out = '';
        if (ca.t === 's') out = shared[Number(v)] ?? '';
        else if (ca.t === 'inlineStr') out = tText(inner);
        else if (ca.t === 'b') out = v === '1' ? 'TRUE' : v ? 'FALSE' : '';
        else if (ca.t === 'e') out = '';
        else if (ca.t === 'str' || ca.t === 'd') out = v;
        else if (v !== '') {
          const n = Number(v);
          if (dateStyle[Number(ca.s || 0)] && !isNaN(n)) out = serialToText(n);
          else if (/e/i.test(v) && Number.isInteger(n)) out = n.toFixed(0);
          else out = v;
        }
        vals[col] = out.trim();
        col++;
      }
      rows[r] = Array.from(vals, x => x || '');
    }
    sheets.push({ name: a.name || `Sheet${sheets.length + 1}`, rows: Array.from(rows, x => x || []) });
  }
  if (!sheets.length) throw new Error('No sheets found');
  return sheets;
}

async function readWorkbook(buf) {
  try {
    return await readWorkbookXml(buf);
  } catch {
    return readWorkbookExcelJS(buf);
  }
}

async function readWorkbookExcelJS(buf) {
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(buf);
  return wb.worksheets.map(ws => {
    const rows = [];
    ws.eachRow({ includeEmpty: true }, (row, n) => {
      const vals = [];
      row.eachCell({ includeEmpty: true }, (cell, col) => {
        // A merged cell repeats its master's value; count it once.
        if (cell.type === ExcelJS.ValueType.Merge) return;
        vals[col - 1] = cellText(cell.value);
      });
      rows[n - 1] = Array.from(vals, v => v || '');
    });
    return { name: ws.name, rows: Array.from(rows, r => r || []) };
  });
}

export function parseDelimited(text) {
  const sample = text.slice(0, 5000).split(/\r?\n/).slice(0, 20);
  const delim = [',', '\t', ';', '|']
    .map(d => [d, sample.reduce((n, l) => n + (l.split(d).length - 1), 0)])
    .sort((a, b) => b[1] - a[1])[0][0];
  const rows = [];
  let row = [], cur = '', inQ = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQ) {
      if (ch === '"') {
        if (text[i + 1] === '"') { cur += '"'; i++; } else inQ = false;
      } else cur += ch;
    } else if (ch === '"' && cur === '') inQ = true;
    else if (ch === delim) { row.push(cur.trim()); cur = ''; }
    else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      row.push(cur.trim()); rows.push(row); row = []; cur = '';
    } else cur += ch;
  }
  if (cur || row.length) { row.push(cur.trim()); rows.push(row); }
  return rows;
}

function decodeText(buf) {
  const bytes = new Uint8Array(buf);
  if (bytes[0] === 0xff && bytes[1] === 0xfe) return new TextDecoder('utf-16le').decode(bytes);
  if (bytes[0] === 0xfe && bytes[1] === 0xff) return new TextDecoder('utf-16be').decode(bytes);
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes).replace(/^﻿/, '');
  } catch {
    return new TextDecoder('windows-1252').decode(bytes);
  }
}

// ─── Values ──────────────────────────────────────────────────────────────────



// iPhone model identifiers seen in device columns → marketing names.
const IPHONE_MODELS = {
  '10,1': 'iPhone 8', '10,4': 'iPhone 8', '10,2': 'iPhone 8 Plus', '10,5': 'iPhone 8 Plus', '10,3': 'iPhone X', '10,6': 'iPhone X',
  '11,2': 'iPhone XS', '11,4': 'iPhone XS Max', '11,6': 'iPhone XS Max', '11,8': 'iPhone XR',
  '12,1': 'iPhone 11', '12,3': 'iPhone 11 Pro', '12,5': 'iPhone 11 Pro Max', '12,8': 'iPhone SE (2nd gen)',
  '13,1': 'iPhone 12 mini', '13,2': 'iPhone 12', '13,3': 'iPhone 12 Pro', '13,4': 'iPhone 12 Pro Max',
  '14,2': 'iPhone 13 Pro', '14,3': 'iPhone 13 Pro Max', '14,4': 'iPhone 13 mini', '14,5': 'iPhone 13', '14,6': 'iPhone SE (3rd gen)',
  '14,7': 'iPhone 14', '14,8': 'iPhone 14 Plus', '15,2': 'iPhone 14 Pro', '15,3': 'iPhone 14 Pro Max',
  '15,4': 'iPhone 15', '15,5': 'iPhone 15 Plus', '16,1': 'iPhone 15 Pro', '16,2': 'iPhone 15 Pro Max',
  '17,1': 'iPhone 16 Pro', '17,2': 'iPhone 16 Pro Max', '17,3': 'iPhone 16', '17,4': 'iPhone 16 Plus', '17,5': 'iPhone 16e',
};
export function modelName(id) {
  const m = String(id || '').match(/iPhone(\d+)[,_](\d+)/);
  return m ? IPHONE_MODELS[`${m[1]},${m[2]}`] || '' : '';
}

// ─── One Apple sheet ─────────────────────────────────────────────────────────

const nonEmpty = row => row.filter(c => c !== '' && c != null).length;
const TITLE_SKIP = /^apple (inc\.?|incorporated)$/i;

// rows: array of arrays of strings. Returns the sheet's title, meta, notes,
// header and data rows, or null if there's no table in it.
export function readAppleSheet(rows, sheetName = '') {
  const meta = { title: '', subtitle: '', dsid: '', phone: '', period: '', timezone: '', identifier: '' };
  const notes = [];
  const width = r => nonEmpty(r);

  // Header row: the first row with 3+ filled cells that isn't a "Report Name:" pair.
  let h = rows.findIndex(r => width(r) >= 3);
  // Small tables (e.g. "Account Key": a "DSID" label over a list of numbers).
  if (h === -1) {
    h = rows.findIndex((r, i) => width(r) >= 1 && width(r) <= 2 && /^[A-Za-z][\w /().-]{0,40}$/.test(r.find(Boolean) || '')
      && rows.slice(i + 1).filter(x => width(x)).length >= 1
      && rows.slice(i + 1).filter(x => width(x)).every(x => /^[\d+ ()-]{5,}$/.test(x.find(Boolean) || '')));
  }

  const lines = rows.slice(0, h === -1 ? rows.length : h).filter(r => width(r)).map(r => r.filter(Boolean).join(' '));
  for (const line of lines) {
    let m;
    if (TITLE_SKIP.test(line)) continue;
    if ((m = line.match(/^DSID:\s*(.+)$/i))) meta.dsid = m[1].trim();
    else if ((m = line.match(/^Phone Number:\s*(.+)$/i))) meta.phone = m[1].trim();
    else if ((m = line.match(/^Period:\s*(.+)$/i))) meta.period = m[1].replace(/\.$/, '').trim();
    else if ((m = line.match(/^Time ?zone:\s*(.+)$/i))) meta.timezone = m[1].trim();
    else if (/^\+?\d{6,}$/.test(line)) meta.identifier = line;
    else if (!meta.title && line.length < 80 && !line.startsWith('*')) meta.title = line;
    else if (!meta.subtitle && line.length < 80 && !line.startsWith('*') && !/:\s/.test(line)) meta.subtitle = line;
    else notes.push(line);
  }
  if (!meta.title) meta.title = sheetName;
  if (h === -1) return { meta, notes, header: [], rows: [] };

  const header = rows[h].map((c, i) => c || `Column ${i + 1}`);
  const body = [];
  for (const r of rows.slice(h + 1)) {
    const n = width(r);
    if (!n) continue;
    // A lone long cell after the table is a footnote, not a record.
    if (n === 1 && header.length > 2 && (r.find(Boolean) || '').length > 40) { notes.push(r.find(Boolean)); continue; }
    body.push(header.map((_, i) => r[i] || ''));
  }

  // Drop columns that hold only "*" (Apple's footnote marker in IDS sheets) or nothing at all with no name.
  const keep = header.map((name, i) => {
    const vals = body.map(r => r[i]).filter(Boolean);
    if (vals.length && vals.every(v => v === '*')) return false;
    return !(/^Column \d+$/.test(name) && !vals.length);
  });

  // Strip a shared "Service=" / "Device Type and OS=" / timestamp="…" prefix from a column.
  const cols = header.map((name, i) => ({ name, index: i })).filter((_, i) => keep[i]);
  for (const c of cols) {
    const vals = body.map(r => r[c.index]).filter(Boolean);
    const prefixes = vals.map(v => (v.match(/^([A-Za-z][A-Za-z ]{0,40})=/) || [])[1]);
    const top = prefixes[0];
    if (top && vals.length && prefixes.filter(p => p === top).length >= vals.length * 0.8) {
      for (const r of body) {
        if (r[c.index].startsWith(top + '=')) r[c.index] = r[c.index].slice(top.length + 1).replace(/^"(.*)"$/s, '$1');
      }
    }
  }

  return {
    meta, notes,
    header: cols.map(c => c.name),
    rows: body.map(r => cols.map(c => r[c.index])),
  };
}

// ─── Mail header files ───────────────────────────────────────────────────────

export function parseMailHeaders(text) {
  const headers = [];
  for (const line of text.split(/\r?\n/)) {
    if (!line.trim()) { if (headers.length) break; continue; }
    if (/^[ \t]/.test(line) && headers.length) headers[headers.length - 1][1] += ' ' + line.trim();
    else {
      const m = line.match(/^([!-9;-~]+):\s*(.*)$/);
      if (m) headers.push([m[1], m[2]]);
      else if (headers.length) headers[headers.length - 1][1] += ' ' + line.trim();
    }
  }
  const get = name => (headers.find(([k]) => k.toLowerCase() === name) || [])[1] || '';
  const received = headers.filter(([k]) => k.toLowerCase() === 'received').map(([, v]) => v);
  const ips = [...new Set(received.join(' ').match(/\b(?:\d{1,3}\.){3}\d{1,3}\b/g) || [])];
  return {
    headers, from: get('from'), to: get('to'), cc: get('cc'), subject: get('subject'),
    date: get('date'), messageId: get('message-id'), replyTo: get('reply-to'), ips,
  };
}

// ─── Classifying what a sheet is ─────────────────────────────────────────────

function kindOf(meta, header, sheetName, path) {
  const t = `${meta.title} ${meta.subtitle} ${sheetName}`.toLowerCase();
  const h = header.map(x => x.toLowerCase());
  if (h.length >= 3 && h[0] === 'column header' && h.includes('column description')) return 'dictionary';
  if (/account details/.test(t)) return 'account';
  if (/icloud account/.test(t) || /_icloud$/i.test(sheetName)) return 'icloudServices';
  if (/imessage capability query/.test(t)) return 'imessageQuery';
  if (/facetime capability query/.test(t)) return 'facetimeQuery';
  if (/facetime call invitation/.test(t)) return 'facetimeInvites';
  if (/icloud mail logs/.test(t)) return 'mailLogs';
  if (/icloud logs/.test(t)) return 'icloudLogs';
  if (/account key/.test(t)) return 'accountKey';
  if (/registration/.test(t)) return 'registrations';
  if (/subscriber/.test(t)) return 'subscriber';
  if (/order activity|transaction/.test(t)) return 'transactions';
  if (/download|update/.test(t)) return 'downloads';
  if (/sign-on|sign on|idms/.test(t)) return 'signOn';
  if (/my apple id|iforgot/.test(t)) return 'appleIdActivity';
  if (/game center/.test(t)) return 'gameCenter';
  if (/gcrm|installed product/.test(t) || (h.includes('serial number') && h.some(x => /imei/.test(x)))) return 'devices';
  if (/icloud mail/i.test(path) && h.includes('message id')) return 'mailMetadata';
  return 'other';
}

export const KIND_LABELS = {
  account: 'Account details', icloudServices: 'iCloud services', icloudLogs: 'iCloud logs',
  imessageQuery: 'iMessage capability queries', facetimeQuery: 'FaceTime capability queries',
  facetimeInvites: 'FaceTime call invitations', mailLogs: 'iCloud Mail logs', mailMetadata: 'iCloud Mail items',
  accountKey: 'Account key', registrations: 'Device registrations', subscriber: 'Media services subscriber',
  transactions: 'Purchases / transactions', downloads: 'Updates & downloads', signOn: 'Apple ID sign-ons',
  appleIdActivity: 'Apple ID & iForgot activity', devices: 'Devices on file (GCRM)', gameCenter: 'Game Center logs',
  other: 'Other records',
};

const DSID_COL = /^(ds ?id|dsid|person_id|ds_id|apple ?id dsid)$/i;

// ─── The whole return ────────────────────────────────────────────────────────

const CATEGORY_DIR = /^(Account Information|Logs - .+|iCloud Mail.*|iCloud Photos.*|iCloud Drive.*|Find My.*|Device.*|Backup.*)$/i;

function accountFolder(path) {
  const parts = path.split('/');
  const i = parts.findIndex(p => CATEGORY_DIR.test(p));
  return i > 0 ? parts[i - 1] : '';
}

export async function parseReturn(entries, onProgress = () => {}) {
  const datasets = [], documents = [], files = [], mail = [], mailSummaries = [], warnings = [];
  const dictionary = {};

  for (let i = 0; i < entries.length; i++) {
    const e = entries[i];
    const lower = e.name.toLowerCase();
    const folder = accountFolder(e.path);
    const record = (status, note = '') => files.push({ path: e.path, size: e.size, status, note });
    onProgress(`Parsing ${i + 1} of ${entries.length}: ${e.name}`);
    try {
      if (/\.(gpg|pgp)$/i.test(lower)) {
        record('skipped', 'Still encrypted: run it through the Apple Legal Process Download tool first.');
        warnings.push(`${e.name} is still GPG-encrypted.`);
      } else if (/\.xlsx$/i.test(lower)) {
        const sheets = await readWorkbook(await e.buffer());
        const read = [];
        let described = 0;
        for (const s of sheets) {
          // Apple's Data Guide: a "Column Header | Column Position | Column Description" table per sheet.
          const dictRow = s.rows.findIndex(r => /^column header$/i.test((r[0] || '').trim()) && r.some(c => /^column description$/i.test((c || '').trim())));
          if (dictRow >= 0) {
            const iD = s.rows[dictRow].findIndex(c => /^column description$/i.test((c || '').trim()));
            const map = dictionary[s.name.toLowerCase()] = dictionary[s.name.toLowerCase()] || {};
            s.rows.slice(dictRow + 1).forEach(r => { if (r[0]) map[r[0].trim().toLowerCase()] = r[iD] || ''; });
            described++;
            continue;
          }
          const sheet = readAppleSheet(s.rows, s.name);
          const kind = kindOf(sheet.meta, sheet.header, s.name, e.path);
          if (!sheet.header.length && !sheet.notes.length) continue;
          datasets.push({ ...sheet, kind, file: e.path, fileName: e.name, sheetName: s.name, folder, source: e.source });
          read.push(`${s.name}: ${sheet.rows.length} rows × ${sheet.header.length} columns`);
        }
        if (described && !read.length) record('parsed', `Data Guide: column descriptions for ${described} sheet${described === 1 ? '' : 's'} (shown as ⓘ on column headers)`);
        else record(read.length ? 'parsed' : 'empty', read.join(' · ') || `${sheets.length} sheet${sheets.length === 1 ? '' : 's'}, no tables`);
      } else if (/\.xls$/i.test(lower)) {
        record('skipped', 'Old .xls format: open it in Excel and save as .xlsx.');
      } else if (/\.pdf$/i.test(lower)) {
        documents.push({ path: e.path, name: e.name, size: e.size, buffer: e.buffer });
        record('document');
      } else if (/account-download-details\.csv$|\.html?$|\.sh$|\.command$/i.test(lower) && /account_data/i.test(e.path)) {
        record('skipped', "Apple's download links and download script, not account data.");
      } else if (/\.(csv|tsv|txt|eml|log)$|^[^.]+$|\d{2}:\d{2}:\d{2}/i.test(lower) || /icloud mail/i.test(e.path)) {
        const text = decodeText(await e.buffer());
        if (/^summary\.txt$/i.test(e.name)) {
          mailSummaries.push({ folder, text: text.trim(), path: e.path });
          record('parsed', 'mail summary');
        } else if (/^[A-Za-z][\w-]*:\s/.test(text.trimStart()) && !/\.(csv|tsv)$/i.test(lower)
                   && /^(from|to|received|return-path|delivered-to|date|subject|message-id)\s*:/im.test(text.slice(0, 4000))) {
          mail.push({ ...parseMailHeaders(text), folder, path: e.path, name: e.name, raw: text });
          record('parsed', 'mail headers');
        } else {
          const rows = parseDelimited(text);
          const sheet = readAppleSheet(rows, e.name);
          if (sheet.header.length) {
            datasets.push({ ...sheet, kind: kindOf(sheet.meta, sheet.header, e.name, e.path), file: e.path, fileName: e.name, sheetName: '', folder, source: e.source });
            record('parsed');
          } else record('skipped', 'No table found');
        }
      } else {
        record('skipped', 'Not a data file');
      }
    } catch (err) {
      record('error', err?.message || String(err));
      warnings.push(`Could not read ${e.name}: ${err?.message || err}`);
    }
  }

  return buildModel({ datasets, documents, files, mail, mailSummaries, warnings, dictionary });
}

// ─── Accounts, timestamps, IPs ───────────────────────────────────────────────

const PRODUCTION = 'Apple production (all accounts)';

function buildModel({ datasets, documents, files, mail, mailSummaries, warnings, dictionary }) {
  const accounts = new Map(); // key → account
  const byDsid = new Map();
  const account = key => {
    if (!accounts.has(key)) accounts.set(key, { key, label: key, dsids: new Set(), phones: new Set(), emails: new Set(), profile: null, services: [], datasets: [] });
    return accounts.get(key);
  };

  // Which account each dataset belongs to: its folder, else its DSID / phone.
  for (const d of datasets) {
    const id = d.meta.dsid && /\d/.test(d.meta.dsid) ? d.meta.dsid : (d.meta.identifier || '');
    d.account = d.folder || d.meta.phone || (id ? `DSID ${id}` : PRODUCTION);
    const a = account(d.account);
    a.datasets.push(d);
    if (id && !d.meta.phone) { a.dsids.add(id); byDsid.set(id, a.key); }
    if (d.meta.phone) a.phones.add(d.meta.phone);
    if (/@/.test(d.folder)) a.emails.add(d.folder);
  }

  for (const d of datasets) {
    const col = name => d.header.findIndex(x => x.toLowerCase() === name.toLowerCase());
    if (d.kind === 'account' && d.rows.length) {
      const a = account(d.account);
      a.profile = d.header.map((k, i) => [k, d.rows[0][i]]);
      const get = k => (a.profile.find(([h]) => h.toLowerCase() === k.toLowerCase()) || [])[1] || '';
      const ds = get('DS ID') || get('DSID');
      if (ds) { a.dsids.add(ds); byDsid.set(ds, a.key); }
      const name = [get('First Name'), get('Last Name')].filter(Boolean).join(' ');
      if (get('Apple ID')) a.emails.add(get('Apple ID'));
      a.name = name;
      a.appleId = get('Apple ID');
    }
    if (d.kind === 'icloudServices') {
      const a = account(d.account);
      a.services = d.rows.map(r => [r[col('iCloud Feature') >= 0 ? col('iCloud Feature') : 2] || r[2], r[r.length - 1]]);
    }
    if (d.kind === 'accountKey') d.rows.forEach(r => r[0] && !byDsid.has(r[0]) && account(PRODUCTION).dsids.add(r[0]));
  }

  // Timestamps and IPs, per dataset and per row.
  for (const d of datasets) {
    d.id = `${d.file}#${d.sheetName}`;
    d.label = KIND_LABELS[d.kind] === KIND_LABELS.other ? (d.meta.subtitle || d.meta.title || d.sheetName || d.fileName) : KIND_LABELS[d.kind];
    d.descriptions = dictionary[(d.sheetName || '').toLowerCase()] || {};
    const sample = d.rows.slice(0, 200);
    const score = (i, test) => {
      const vals = sample.map(r => r[i]).filter(Boolean);
      return vals.length ? vals.filter(test).length / vals.length : 0;
    };
    d.ipCols = d.header.map((_, i) => i).filter(i => score(i, isIp) >= 0.6);
    const timeCols = d.header.map((_, i) => i).filter(i => score(i, v => parseAppleTime(v, d.meta.timezone)) >= 0.6);
    const named = timeCols.filter(i => /time|timestamp|activity|_ts$|^date|date$/i.test(d.header[i]));
    d.timeCol = (named.length ? named : timeCols).find(i => sample.some(r => (parseAppleTime(r[i], d.meta.timezone) || {}).dateOnly === false));
    if (d.timeCol === undefined) d.timeCol = named[0] ?? timeCols[0] ?? -1;
    d.dsidCol = d.header.findIndex(x => DSID_COL.test(x.trim()));
    // Each row's time comes from the main time column, or the next one that has a value
    // (e.g. a registration with no activation date still has the date it was received).
    const order = [...new Set([d.timeCol, ...named, ...timeCols])].filter(i => i >= 0);
    d.times = d.timeCol >= 0 ? d.rows.map(r => {
      for (const c of order) {
        const t = parseAppleTime(r[c], d.meta.timezone);
        if (t) return { ...t, col: c };
      }
      return null;
    }) : [];
    d.assumedUtc = d.times.some(t => t && t.assumedUtc);
    // Rows in the production workbook belong to whichever account their DSID says.
    d.rowAccounts = d.rows.map(r => (d.account === PRODUCTION && d.dsidCol >= 0 && byDsid.get(r[d.dsidCol])) || d.account);
  }

  // Two sheets with the same label in one account (e.g. iTunes downloads and AMS updates): add the sheet name.
  const seen = {};
  datasets.forEach(d => { const k = `${d.account}|${d.label}`; seen[k] = (seen[k] || 0) + 1; });
  datasets.forEach(d => {
    if (seen[`${d.account}|${d.label}`] > 1 && d.sheetName) d.label = `${d.label} (${d.sheetName.replace(/_Account$/i, '')})`;
  });

  for (const m of mail) {
    m.account = m.folder || PRODUCTION;
    account(m.account);
    m.time = parseAppleTime(m.date, '');
  }
  for (const s of mailSummaries) account(s.folder || PRODUCTION);

  // Accounts with no data of their own (e.g. the production bucket when everything was attributed) stay listed only if they have datasets.
  const list = [...accounts.values()]
    .filter(a => a.datasets.length || mail.some(m => m.account === a.key) || a.key !== PRODUCTION)
    .map(a => ({ ...a, dsids: [...a.dsids], phones: [...a.phones], emails: [...a.emails] }))
    .sort((x, y) => (x.key === PRODUCTION) - (y.key === PRODUCTION) || x.label.localeCompare(y.label));

  return { accounts: list, datasets, documents, files, mail, mailSummaries, warnings, production: PRODUCTION };
}

// ─── Derived views ───────────────────────────────────────────────────────────

export function ipSummary(model) {
  const map = new Map();
  const add = (ip, t, source, account) => {
    const key = ip.trim().toLowerCase();
    if (!map.has(key)) map.set(key, { ip: ip.trim(), count: 0, first: null, last: null, sources: new Set(), accounts: new Set() });
    const r = map.get(key);
    r.count++;
    if (t) { r.first = r.first == null ? t : Math.min(r.first, t); r.last = r.last == null ? t : Math.max(r.last, t); }
    r.sources.add(source);
    r.accounts.add(account);
  };
  for (const d of model.datasets) {
    // Account Details has one row: its creation date isn't when the IP was seen, so give it no time.
    const timed = d.kind !== 'account';
    d.rows.forEach((r, i) => d.ipCols.forEach(c => {
      if (isIp(r[c])) add(r[c], timed ? d.times[i]?.t : null, timed ? d.label : `${d.label}: ${d.header[c]}`, d.rowAccounts[i]);
    }));
  }
  for (const m of model.mail) m.ips.forEach(ip => add(ip, m.time?.t, 'iCloud Mail headers', m.account));
  return [...map.values()].map(r => ({ ...r, sources: [...r.sources], accounts: [...r.accounts] }));
}

// Every row with a time, as one list.
export function timeline(model) {
  const out = [];
  for (const d of model.datasets) {
    if (d.timeCol < 0) continue;
    const show = d.header.map((h, i) => i).filter(i => i !== d.timeCol && i !== d.dsidCol);
    d.rows.forEach((r, i) => {
      const t = d.times[i];
      if (!t) return;
      const detail = show.filter(c => r[c] && r[c] !== '0').slice(0, 6).map(c => `${d.header[c]}: ${r[c]}`).join(' · ');
      out.push({ t: t.t, dateOnly: t.dateOnly, raw: r[t.col], account: d.rowAccounts[i], source: d.label, kind: d.kind, detail });
    });
  }
  for (const m of model.mail) {
    if (m.time) out.push({ t: m.time.t, raw: m.date, account: m.account, source: 'iCloud Mail headers', kind: 'mail', detail: `From: ${m.from} · To: ${m.to}${m.subject ? ` · Subject: ${m.subject}` : ''}` });
  }
  return out.sort((a, b) => a.t - b.t);
}

// Handles looked up in iMessage / FaceTime capability queries.
export function handleSummary(model) {
  const map = new Map();
  for (const d of model.datasets) {
    if (!['imessageQuery', 'facetimeQuery', 'facetimeInvites'].includes(d.kind)) continue;
    const col = d.header.findIndex(h => /lookup handle|recipient handle/i.test(h));
    if (col < 0) continue;
    const service = d.kind === 'imessageQuery' ? 'iMessage' : 'FaceTime';
    d.rows.forEach((r, i) => {
      const handle = cleanHandle(r[col]);
      if (!handle) return;
      const key = handle.toLowerCase();
      if (!map.has(key)) map.set(key, { handle, count: 0, first: null, last: null, services: new Set(), accounts: new Set(), raw: r[col] });
      const x = map.get(key);
      x.count++;
      const t = d.times[i]?.t;
      if (t) { x.first = x.first == null ? t : Math.min(x.first, t); x.last = x.last == null ? t : Math.max(x.last, t); }
      x.services.add(service);
      x.accounts.add(d.rowAccounts[i]);
    });
  }
  return [...map.values()].map(x => ({ ...x, services: [...x.services], accounts: [...x.accounts] }));
}

// Devices: service records and registrations by serial, device IDs (GUID/UDID)
// from purchases, sign-ons and Game Center, and device types seen in logs.
export function deviceSummary(model) {
  const bySerial = new Map();
  const models = new Map();
  const guids = new Map();
  for (const d of model.datasets) {
    const h = d.header.map(x => x.toLowerCase());
    const iGuid = h.findIndex(x => /global_unique_id|guid|unique_device_id|udid/.test(x));
    if (iGuid >= 0) {
      const iModel = h.findIndex(x => /device_version|device_type_name/.test(x));
      d.rows.forEach((r, n) => {
        const g = r[iGuid];
        if (!g) return;
        if (!guids.has(g)) guids.set(g, { guid: g, count: 0, first: null, last: null, models: new Set(), sources: new Set(), accounts: new Set() });
        const x = guids.get(g);
        x.count++;
        const t = d.times[n]?.t;
        if (t) { x.first = x.first == null ? t : Math.min(x.first, t); x.last = x.last == null ? t : Math.max(x.last, t); }
        if (iModel >= 0 && r[iModel]) x.models.add(r[iModel]);
        x.sources.add(d.label);
        x.accounts.add(d.rowAccounts[n]);
      });
    }
    const iSerial = h.findIndex(x => /^serial[ -]number/.test(x));
    const iImei = h.findIndex(x => /^imei number$|^imei$/.test(x));
    const iImei2 = h.findIndex(x => /imei number2|imei2/.test(x));
    const iMeid = h.findIndex(x => x === 'meid');
    const iDesc = h.findIndex(x => /product description/.test(x));
    d.rows.forEach((r, n) => {
      if (iSerial >= 0 && r[iSerial] && r[iSerial] !== '*') {
        const s = r[iSerial];
        if (!bySerial.has(s)) bySerial.set(s, { serial: s, imei: new Set(), meid: new Set(), description: new Set(), sources: new Set(), accounts: new Set(), count: 0 });
        const x = bySerial.get(s);
        x.count++;
        [r[iImei], r[iImei2]].forEach(v => v && x.imei.add(v));
        if (iMeid >= 0 && r[iMeid]) x.meid.add(r[iMeid]);
        if (iDesc >= 0 && r[iDesc]) x.description.add(r[iDesc]);
        x.sources.add(d.label);
        x.accounts.add(d.rowAccounts[n]);
      }
    });
    // Device type columns in logs ("iPhone15,3", "21A360; iPhone15,3", "<iPhone15,3> <iPhone OS;17.0.3;21A360> …")
    const devCols = h.map((x, i) => i).filter(i => /device type|^model$|device_version|device_type_name/.test(h[i])
      || d.rows.slice(0, 50).some(r => /^iPhone\d+_\d+$/.test(r[i] || '')));
    d.rows.forEach((r, n) => devCols.forEach(c => {
      const ids = (r[c] || '').match(/(iPhone|iPad|iPod|Watch|MacBook\w*|iMac|Mac)\d+[,_]\d+/g);
      (ids || []).forEach(id => {
        const key = id.replace('_', ',');
        if (!models.has(key)) models.set(key, { id: key, name: modelName(key), count: 0, accounts: new Set(), sources: new Set() });
        const x = models.get(key);
        x.count++;
        x.accounts.add(d.rowAccounts[n]);
        x.sources.add(d.label);
      });
    }));
  }
  const flat = x => Object.fromEntries(Object.entries(x).map(([k, v]) => [k, v instanceof Set ? [...v] : v]));
  return { serials: [...bySerial.values()].map(flat), models: [...models.values()].map(flat), guids: [...guids.values()].map(flat) };
}
